import { Button, createTableColumn, DataGrid, DataGridBody, DataGridCell, DataGridHeader, DataGridHeaderCell, DataGridRow, Image, makeStyles, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, SkeletonItem, TableColumnDefinition, Text, tokens } from '@fluentui/react-components';
import { DeleteFilled, MoreHorizontalFilled, ArrowDownloadFilled, GridRegular } from '@fluentui/react-icons';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { IAuthenticatedImage, useAuthenticateImages } from '@tdp/common/authenticatedImages';

import { IAppSummary } from '../abstractions/IAppSummary';

const useStyles = makeStyles({
  container: {
    width: "100%",
  },
  header: {
    height: "48px",
  },
  appName: {
    display: 'flex',
    flexGrow: 1,
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalXS,
  },
  icon: {
    borderRadius: tokens.borderRadiusMedium,
  },
  textColor: {
    color: tokens.colorBrandForeground2,
  }
});

function AppListIcon({ image, altText }: { image: IAuthenticatedImage, altText: string }) {
  const intl = useIntl();
  if (image.type === 'error') {
    return (
      <Image src={"images/home/defaultIcon.png"} alt={altText} width={32} height={32} />
    )
  } else if (image.type == 'loading') {
    return (
      <SkeletonItem shape="square" size={32} aria-label={intl.formatMessage({
        defaultMessage: "Loading app icon",
        description: "Alternate text for pre-loaded app icon in the app list grid"
      })} />
    )
  }

  return (
    <Image src={image.dataUri} alt={altText} width={32} height={32} />
  )
}

interface IAppListDataGridElement {
  appName?: JSX.Element;
  version?: string | null;
  appId?: string | null;
  updatedAt?: string | null;
  name?: string | null;
}

const AppListDataGrid: React.FC<{
  apps: IAppSummary[];
  showDeleteDialog: (appId: string) => void
  onDownload: (appId: string) => void
  onDuplicate: (appId: string) => void
}> = ({ onDownload, showDeleteDialog, apps, onDuplicate }) => {

  const intl = useIntl();
  const navigate = useNavigate();
  const classes = useStyles();
  const columnSizingOptions = {
    appName: {
      minWidth: 240,
      defaultWidth: 380,
    },
    version: {
      minWidth: 80,
      defaultWidth: 140,
    },
    appId: {
      minWidth: 240,
      defaultWidth: 360,
    },
    lastUpdated: {
      minWidth: 180,
      defaultWidth: 240,
    }
  };
  const appImages = useAuthenticateImages(apps.map(app => `/appdefinitions/${app.appId}/icons/color`));
  const items: IAppListDataGridElement[] = apps.map((app, idx) => {
    const appImage = appImages[idx];
    const formattedDate = app.updatedAt ? new Date(app.updatedAt).toLocaleDateString(
      'en-US',
      { year: 'numeric', month: 'long', day: 'numeric' }
    ) : 'N/A';
    return {
      appName: (
        <div className={classes.appName}>
          <AppListIcon image={appImage} altText={app.appName ?? ''} />
          <Text className={classes.textColor}>
            {app.appName}
          </Text>
        </div>
      ),
      version: app.version,
      appId: app.teamsAppId,
      updatedAt: formattedDate,
      name: app.appName
    };
  });

  const columns: TableColumnDefinition<IAppListDataGridElement>[] = [
    createTableColumn<IAppListDataGridElement>({
      columnId: "appName",
      renderHeaderCell: () => intl.formatMessage({ defaultMessage: "App name" }),
      renderCell: (data) => <Button appearance='transparent' onClick={() => { itemOnClick(data.appId ?? ''); }}>{data.appName}</Button>
    }),
    createTableColumn<IAppListDataGridElement>({
      columnId: "version",
      renderHeaderCell: () => intl.formatMessage({ defaultMessage: "Version" }),
      renderCell: (data) => data.version
    }),
    createTableColumn<IAppListDataGridElement>({
      columnId: "appId",
      renderHeaderCell: () => intl.formatMessage({ defaultMessage: "App ID" }),
      renderCell: (data) => data.appId
    }),
    createTableColumn<IAppListDataGridElement>({
      columnId: "lastUpdated",
      renderHeaderCell: () => intl.formatMessage({ defaultMessage: "Last updated" }),
      renderCell: (data) => data.updatedAt
    }),
    createTableColumn<IAppListDataGridElement>({
      columnId: "action",
      renderHeaderCell: () => intl.formatMessage({ defaultMessage: "Action" }),
      renderCell: (data) => <Elipsis actions={[
        {
          onAction: () => { onDuplicate(data.appId ?? ''); },
          icon: <GridRegular />,
          title: intl.formatMessage({ defaultMessage: "Duplicate" }),
        },
        {
          onAction: () => { onDownload(data.appId ?? ''); },
          icon: <ArrowDownloadFilled />,
          title: intl.formatMessage({ defaultMessage: "Download app package" }),
        },
        {
          onAction: () => { showDeleteDialog(data.appId ?? ''); },
          icon: <DeleteFilled />,
          title: intl.formatMessage({ defaultMessage: "Delete" }),
        }
      ]} />
    }),
  ]

  const Elipsis: React.FC<{
    actions: {
      onAction: any;
      icon: JSX.Element;
      title: string;
      isDisabled?: boolean;
    }[];
  }> = ({ actions }) => {
    return (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button icon={<MoreHorizontalFilled />} appearance="transparent" />
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {
              actions.map((action, index) => (
                <MenuItem key={index} icon={action.icon} onClick={action.onAction} disabled={action.isDisabled}>
                  {action.title}
                </MenuItem>
              ))
            }
          </MenuList>
        </MenuPopover>
      </Menu>
    )
  };

  const itemOnClick = (item: string) => {
    navigate(`/apps/${item}/dashboard`); //TODO(nibeauli): change with route to actual overview page.
  }

  return (
    <DataGrid
      items={items}
      columns={columns}
      className={classes.container}
      resizableColumns
      columnSizingOptions={columnSizingOptions}
      focusMode='composite'
    >
      <DataGridHeader>
        <DataGridRow className={classes.header}>
          {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
        </DataGridRow>
      </DataGridHeader>
      <DataGridBody<IAppListDataGridElement>>
        {({ item, rowId }) => (
          <DataGridRow<IAppListDataGridElement> key={rowId}>
            {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
          </DataGridRow>
        )}
      </DataGridBody>
    </DataGrid>
  )
};

export default AppListDataGrid;
